import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Shield, Plus, Pencil, Eye, EyeOff, Check, X, ChevronDown, ChevronUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERMISSION_MODULES = [
  { key: 'empresas', label: 'Empresas' },
  { key: 'usuarios_tenant', label: 'Usuarios do Tenant' },
  { key: 'operadores', label: 'Operadores' },
  { key: 'maquinas', label: 'Maquinas' },
  { key: 'locais', label: 'Locais de Operacao' },
  { key: 'tipos_operacao', label: 'Tipos de Operacao' },
  { key: 'ordens_servico', label: 'Ordens de Servico' },
  { key: 'combustivel', label: 'Combustivel' },
  { key: 'producao', label: 'Producao' },
  { key: 'relatorios', label: 'Relatorios' },
  { key: 'alterar_tipo_operacao', label: 'Alterar Tipo de Operacao (O.S.)' },
];

const ACTIONS = [
  { key: 'visualizar', label: 'Visualizar' },
  { key: 'criar', label: 'Criar' },
  { key: 'editar', label: 'Editar' },
];

const emptyPermissions = () => {
  const p = {};
  PERMISSION_MODULES.forEach(m => { p[m.key] = { visualizar: false, criar: false, editar: false }; });
  return p;
};

const fullPermissions = () => {
  const p = {};
  PERMISSION_MODULES.forEach(m => { p[m.key] = { visualizar: true, criar: true, editar: true }; });
  return p;
};

const emptyForm = { name: '', email: '', password: '', cpf: '', phone: '', permissions: emptyPermissions() };

export default function MasterUsers() {
  const { getHeaders } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);
  const [promoteEmail, setPromoteEmail] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/master-users`, { headers: getHeaders() });
      if (res.ok) setUsers(await res.json());
      else toast.error('Erro ao carregar usuarios');
    } catch { toast.error('Erro de conexao'); }
    setLoading(false);
  }, [getHeaders]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(true);
    setShowPassword(false);
  };

  const openEdit = (u) => {
    setEditingId(u.id);
    setForm({ name: u.name, email: u.email, password: '', cpf: u.cpf || '', phone: u.phone || '', permissions: u.permissions || emptyPermissions() });
    setShowForm(true);
    setShowPassword(false);
  };

  const closeForm = () => { setShowForm(false); setEditingId(null); setForm(emptyForm); };

  const togglePermission = (mod, action) => {
    setForm(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [mod]: { ...prev.permissions[mod], [action]: !prev.permissions[mod][action] }
      }
    }));
  };

  const grantAll = () => setForm(prev => ({ ...prev, permissions: fullPermissions() }));
  const revokeAll = () => setForm(prev => ({ ...prev, permissions: emptyPermissions() }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error('Nome e email sao obrigatorios'); return; }
    if (!editingId && !form.password) { toast.error('Senha e obrigatoria para novo usuario'); return; }

    setSaving(true);
    try {
      const body = { ...form };
      if (editingId && !body.password) delete body.password;

      const url = editingId ? `${API_URL}/api/master-users/${editingId}` : `${API_URL}/api/master-users`;
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success(editingId ? 'Usuario atualizado!' : 'Usuario criado!');
        setPromoteEmail(false);
        closeForm();
        fetchUsers();
      } else if (res.status === 409 && !editingId) {
        setPromoteEmail(true);
        toast.info('Confirme a promocao abaixo no formulario');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erro ao salvar');
      }
    } catch { toast.error('Erro de conexao'); }
    setSaving(false);
  };

  const handlePromote = async () => {
    setSaving(true);
    try {
      const body = { ...form, promote_from_user: true };
      const res = await fetch(`${API_URL}/api/master-users`, {
        method: 'POST',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success('Usuario promovido a Auxiliar Master!');
        setPromoteEmail(false);
        closeForm();
        fetchUsers();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erro ao promover');
      }
    } catch { toast.error('Erro de conexao'); }
    setSaving(false);
  };

  const toggleStatus = async (u) => {
    const newStatus = u.status === 'active' ? 'inactive' : 'active';
    try {
      const res = await fetch(`${API_URL}/api/master-users/${u.id}`, {
        method: 'PUT',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        toast.success(newStatus === 'active' ? 'Usuario ativado!' : 'Usuario inativado!');
        fetchUsers();
      } else toast.error('Erro ao alterar status');
    } catch { toast.error('Erro de conexao'); }
  };

  const countPermissions = (perms) => {
    let total = 0, granted = 0;
    Object.values(perms || {}).forEach(acts => {
      Object.values(acts).forEach(v => { total++; if (v) granted++; });
    });
    return { total, granted };
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-10 h-10 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="master-users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="text-yellow-500" size={28} />
          <div>
            <h1 className="text-2xl font-bold text-white">Usuarios Master</h1>
            <p className="text-gray-500 text-sm">Gerencie seus usuarios auxiliares</p>
          </div>
        </div>
        <button onClick={openCreate} data-testid="create-master-user-btn"
          className="flex items-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg transition-colors">
          <Plus size={18} /> Novo Auxiliar
        </button>
      </div>

      {/* Users List */}
      <div className="bg-[#111] border border-gray-800 rounded-xl overflow-hidden">
        {users.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Nenhum usuario auxiliar cadastrado.</div>
        ) : (
          <div className="divide-y divide-gray-800">
            {users.map(u => {
              const { total, granted } = countPermissions(u.permissions);
              const isExpanded = expandedUser === u.id;
              return (
                <div key={u.id} className="hover:bg-gray-900/50 transition-colors">
                  <div className="flex items-center gap-4 p-4">
                    <div className="w-10 h-10 rounded-full bg-yellow-500/10 flex items-center justify-center text-yellow-500 font-bold shrink-0">
                      {u.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{u.name}</p>
                      <p className="text-gray-500 text-sm truncate">{u.email}</p>
                    </div>
                    <div className="hidden sm:flex items-center gap-2">
                      <span className="text-xs text-gray-400 bg-gray-800 px-2 py-1 rounded">{granted}/{total} permissoes</span>
                      <span className={`text-xs px-2 py-1 rounded font-medium ${u.status === 'active' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                        {u.status === 'active' ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setExpandedUser(isExpanded ? null : u.id)} data-testid={`expand-user-${u.id}`}
                        className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800 transition-colors">
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                      <button onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}
                        className="p-2 text-gray-400 hover:text-yellow-500 rounded-lg hover:bg-gray-800 transition-colors">
                        <Pencil size={16} />
                      </button>
                      <button onClick={() => toggleStatus(u)} data-testid={`toggle-status-${u.id}`}
                        className={`p-2 rounded-lg hover:bg-gray-800 transition-colors ${u.status === 'active' ? 'text-green-400 hover:text-red-400' : 'text-red-400 hover:text-green-400'}`}>
                        {u.status === 'active' ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  {/* Expanded permissions view */}
                  {isExpanded && (
                    <div className="px-4 pb-4">
                      <div className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-2 font-medium uppercase">Matriz de Permissoes</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {PERMISSION_MODULES.map(mod => (
                            <div key={mod.key} className="flex items-center justify-between bg-[#111] rounded px-3 py-2">
                              <span className="text-sm text-gray-300">{mod.label}</span>
                              <div className="flex gap-2">
                                {ACTIONS.map(act => (
                                  <span key={act.key} className={`text-xs px-1.5 py-0.5 rounded ${u.permissions?.[mod.key]?.[act.key] ? 'bg-yellow-500/10 text-yellow-500' : 'bg-gray-800 text-gray-600'}`}>
                                    {act.label.charAt(0)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-[#111] border border-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4" data-testid="master-user-form">
            <div className="flex items-center justify-between p-4 border-b border-gray-800">
              <h2 className="text-lg font-bold text-white">{editingId ? 'Editar Auxiliar' : 'Novo Auxiliar Master'}</h2>
              <button onClick={closeForm} className="p-2 text-gray-400 hover:text-white"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {/* Basic fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Nome *</label>
                  <input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    data-testid="input-name" className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-yellow-500 outline-none" />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Email *</label>
                  <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                    data-testid="input-email" className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-yellow-500 outline-none" />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Senha {editingId ? '(deixe vazio para manter)' : '*'}</label>
                  <div className="relative">
                    <input type={showPassword ? 'text' : 'password'} value={form.password}
                      onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                      data-testid="input-password" className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-yellow-500 outline-none pr-10" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">CPF</label>
                  <input type="text" value={form.cpf} onChange={e => setForm(p => ({ ...p, cpf: e.target.value }))}
                    data-testid="input-cpf" className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-yellow-500 outline-none" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-sm text-gray-400 mb-1 block">Telefone</label>
                  <input type="text" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                    data-testid="input-phone" className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-yellow-500 outline-none" />
                </div>
              </div>

              {/* Permissions Matrix */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-semibold">Matriz de Permissoes</h3>
                  <div className="flex gap-2">
                    <button type="button" onClick={grantAll} data-testid="grant-all-btn"
                      className="text-xs px-3 py-1.5 bg-green-500/10 text-green-400 hover:bg-green-500/20 rounded-lg transition-colors">
                      Conceder Todas
                    </button>
                    <button type="button" onClick={revokeAll} data-testid="revoke-all-btn"
                      className="text-xs px-3 py-1.5 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors">
                      Revogar Todas
                    </button>
                  </div>
                </div>

                <div className="bg-[#0A0A0A] border border-gray-800 rounded-lg overflow-hidden">
                  {/* Header */}
                  <div className="grid grid-cols-[1fr_repeat(3,80px)] gap-0 px-4 py-2 border-b border-gray-800">
                    <span className="text-xs text-gray-500 font-medium uppercase">Modulo</span>
                    {ACTIONS.map(a => (
                      <span key={a.key} className="text-xs text-gray-500 font-medium uppercase text-center">{a.label}</span>
                    ))}
                  </div>
                  {/* Rows */}
                  {PERMISSION_MODULES.map(mod => (
                    <div key={mod.key} className="grid grid-cols-[1fr_repeat(3,80px)] gap-0 px-4 py-2.5 border-b border-gray-800/50 hover:bg-gray-900/30">
                      <span className="text-sm text-gray-300">{mod.label}</span>
                      {ACTIONS.map(act => (
                        <div key={act.key} className="flex justify-center">
                          <button type="button" onClick={() => togglePermission(mod.key, act.key)}
                            data-testid={`perm-${mod.key}-${act.key}`}
                            className={`w-7 h-7 rounded-md flex items-center justify-center transition-colors ${
                              form.permissions[mod.key]?.[act.key]
                                ? 'bg-yellow-500 text-black'
                                : 'bg-gray-800 text-gray-600 hover:bg-gray-700'
                            }`}>
                            {form.permissions[mod.key]?.[act.key] ? <Check size={14} /> : <X size={14} />}
                          </button>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  Auxiliares sempre podem ver a Visao Geral e acessar tenants. Exclusao de registros e sempre bloqueada.
                </p>
              </div>

              {/* Promote Banner */}
              {promoteEmail && (
                <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg p-4" data-testid="promote-banner">
                  <p className="text-amber-400 font-semibold text-sm mb-2">Este email ja esta vinculado a um tenant</p>
                  <p className="text-gray-300 text-xs mb-3">Ao confirmar, o vinculo com o tenant sera removido e o usuario sera promovido a Auxiliar Master. Registros historicos (O.S., abastecimentos, logs) serao preservados.</p>
                  <button type="button" onClick={handlePromote} disabled={saving} data-testid="confirm-promote-btn"
                    className="px-5 py-2 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-lg transition-colors disabled:opacity-50 text-sm">
                    {saving ? 'Promovendo...' : 'Confirmar Promocao para Auxiliar Master'}
                  </button>
                </div>
              )}

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => { setPromoteEmail(false); closeForm(); }} className="px-4 py-2 text-gray-400 hover:text-white transition-colors">
                  Cancelar
                </button>
                <button type="submit" disabled={saving || promoteEmail} data-testid="save-master-user-btn"
                  className="px-6 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg transition-colors disabled:opacity-50">
                  {saving ? 'Salvando...' : (editingId ? 'Salvar' : 'Criar Auxiliar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
